import React, {useState, useRef, useEffect} from "react";
import {Button, InputGroup} from "react-bootstrap";

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const formatDate = (year, month, day) => {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
};

const parseDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return {year: y, month: m - 1, day: d};
};

const DatePicker = ({value, onChange, label, name}) => {
  // onChange が渡されない場合は、内部 state で committed value を持つ
  // (フォーム送信用に <input type="hidden" name=...> を出す用途を想定)
  const [open, setOpen] = useState(false);
  const [committedValue, setCommittedValue] = useState(value || '');
  const effectiveValue = onChange ? value : committedValue;
  const handleConfirm = (newVal) => {
    if (onChange) onChange(newVal);
    else setCommittedValue(newVal);
  };
  const parsed = parseDate(effectiveValue);
  const now = new Date();
  const [viewYear, setViewYear] = useState(parsed?.year || now.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? now.getMonth());
  const [selected, setSelected] = useState(effectiveValue || '');
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const selectDay = (day) => {
    setSelected(formatDate(viewYear, viewMonth, day));
  };

  const confirm = () => {
    handleConfirm(selected);
    setOpen(false);
  };

  const clear = () => {
    setSelected('');
    handleConfirm('');
    setOpen(false);
  };

  const days = daysInMonth(viewYear, viewMonth);
  const firstDay = firstDayOfMonth(viewYear, viewMonth);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  const selectedParsed = parseDate(selected);
  const isSelected = (day) =>
    selectedParsed && selectedParsed.year === viewYear && selectedParsed.month === viewMonth && selectedParsed.day === day;

  return (
    <div ref={ref} style={{position: 'relative', display: 'inline-block'}}>
      {name && <input type="hidden" name={name} value={effectiveValue || ''} />}
      <InputGroup className="align-items-center" style={{cursor: 'pointer'}} onClick={() => setOpen(!open)}>
        {label && <InputGroup.Text>{label}</InputGroup.Text>}
        <div className="form-control d-flex align-items-center justify-content-between" style={{minWidth: 160, minHeight: 38, background: '#fff'}}>
          <span>{effectiveValue || <span style={{color: '#999'}}>日付を選択</span>}</span>
          <i className="fas fa-calendar-alt" style={{color: '#666'}} />
        </div>
      </InputGroup>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 1050,
          background: '#fff', border: '1px solid #ccc', borderRadius: 4,
          padding: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', minWidth: 260
        }}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
            <Button size="sm" variant="outline-secondary" onClick={prevMonth}>&lt;</Button>
            <span style={{fontWeight: 'bold'}}>{viewYear}年{viewMonth + 1}月</span>
            <Button size="sm" variant="outline-secondary" onClick={nextMonth}>&gt;</Button>
          </div>
          <table style={{width: '100%', textAlign: 'center', fontSize: 13}}>
            <thead>
              <tr>
                {WEEKDAYS.map(w => <th key={w} style={{padding: 2, color: w === '日' ? '#c00' : w === '土' ? '#00c' : '#333'}}>{w}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.from({length: Math.ceil(cells.length / 7)}, (_, row) => (
                <tr key={row}>
                  {cells.slice(row * 7, row * 7 + 7).map((day, i) => (
                    <td key={i} style={{padding: 2}}>
                      {day && (
                        <div
                          onClick={() => selectDay(day)}
                          style={{
                            cursor: 'pointer', borderRadius: 4, padding: '4px 0',
                            background: isSelected(day) ? '#0d6efd' : 'transparent',
                            color: isSelected(day) ? '#fff' : 'inherit',
                          }}>
                          {day}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 8}}>
            <Button size="sm" variant="outline-danger" onClick={clear}>クリア</Button>
            <Button size="sm" variant="primary" onClick={confirm}>確定</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
